import { ReactElement, useEffect, useState } from 'react';
import styles from './ValkeyDashboard.module.less';
import { Col, Row } from 'antd';
import usePrometheusValkeyCommandsPerSecStats from '../../../core/hooks/usePrometheusValkeyCommandsPerSecStats';
import usePrometheusValkeyTotalKeysStats from '../../../core/hooks/usePrometheusValkeyTotalKeysStats';
import usePrometheusValkeyConnectedClientsStats from '../../../core/hooks/usePrometheusValkeyConnectedClientsStats';
import usePrometheusValkeyBlockedClientsStats from '../../../core/hooks/usePrometheusValkeyBlockedClientsStats';
import usePrometheusValkeyMemoryUsedStats from '../../../core/hooks/usePrometheusValkeyMemoryUsedStats';
import usePrometheusValkeySlaveLagStats from '../../../core/hooks/usePrometheusValkeySlaveLagStats';
import PrometheusValkeyBlockedClientsChart from '../../ccx/charts/PrometheusValkeyBlockedClientsChart';
import PrometheusValkeyMemoryUsedChart from '../../ccx/charts/PrometheusValkeyMemoryUsedChart';
import PrometheusValkeyConnectedClientsChart from '../../ccx/charts/PrometheusValkeyConnectedClientsChart';
import PrometheusValkeyCommandsPerSecChart from '../../ccx/charts/PrometheusValkeyCommandsPerSecChart';
import PrometheusValkeyTotalKeysChart from '../../ccx/charts/PrometheusValkeyTotalKeysChart';
import PrometheusValkeySlaveLagChart from '../../ccx/charts/PrometheusValkeySlaveLagChart';
import CcxComponentProps from '../../../core/CcxComponent';
import CloudInstance from '../../../types/CloudInstance';

interface Props extends CcxComponentProps {
    uuid: string;
    host_uuid: string;
    port: number;
    from?: string;
    to?: string;
    interval: number;
    node?: CloudInstance;
    hidden?: boolean;
    displaySummary?: boolean;
    onLoading?: Function;
}

function ValkeyDashboard({
    uuid,
    host_uuid,
    port,
    from,
    to,
    interval,
    node,
    hidden,
    displaySummary,
    onLoading,
    testId = 'ValkeyDashboard',
}: Props): ReactElement {
    const {
        stats: commandsPerSecStats,
        refresh: commandsPerSecStatsRefresh,
        loading: commandsPerSecStatsLoading,
    } = usePrometheusValkeyCommandsPerSecStats(uuid, host_uuid, from, to);

    const {
        stats: totalKeysStats,
        refresh: totalKeysStatsRefresh,
        loading: totalKeysStatsLoading,
    } = usePrometheusValkeyTotalKeysStats(uuid, host_uuid, from, to);

    const {
        stats: connectedClientsStats,
        refresh: connectedClientsStatsRefresh,
        loading: connectedClientsStatsLoading,
    } = usePrometheusValkeyConnectedClientsStats(uuid, host_uuid, from, to);

    const {
        stats: blockedClientsStats,
        refresh: blockedClientsStatsRefresh,
        loading: blockedClientsStatsLoading,
    } = usePrometheusValkeyBlockedClientsStats(uuid, host_uuid, from, to);

    const {
        stats: memoryUsedStats,
        refresh: memoryUsedStatsRefresh,
        loading: memoryUsedStatsLoading,
    } = usePrometheusValkeyMemoryUsedStats(uuid, host_uuid, from, to);

    const {
        stats: slaveLagStats,
        refresh: slaveLagStatsRefresh,
        loading: slaveLagStatsLoading,
    } = usePrometheusValkeySlaveLagStats(uuid, host_uuid, from, to);

    const [commandsPerSecStatsInterval, setCommandsPerSecStatsInterval] =
        useState<number | undefined>(undefined);
    const [totalKeysStatsInterval, setTotalKeysStatsInterval] = useState<
        number | undefined
    >(undefined);
    const [connectedClientsStatsInterval, setConnectedClientsStatsInterval] =
        useState<number | undefined>(undefined);
    const [blockedClientsStatsInterval, setBlockedClientsStatsInterval] =
        useState<number | undefined>(undefined);
    const [memoryUsedStatsInterval, setMemoryUsedStatsInterval] = useState<
        number | undefined
    >(undefined);
    const [slaveLagStatsInterval, setSlaveLagStatsInterval] = useState<
        number | undefined
    >(undefined);

    const [refreshInterval, setRefreshInterval] = useState(interval);

    useEffect(() => {
        let interval: any;
        if (commandsPerSecStats && refreshInterval > 0) {
            interval = setInterval(() => {
                commandsPerSecStatsRefresh();
            }, refreshInterval);
            setCommandsPerSecStatsInterval(interval);
        }
        return () => clearInterval(interval);
    }, [commandsPerSecStats, refreshInterval]);

    useEffect(() => {
        let interval: any;
        if (totalKeysStats && refreshInterval > 0) {
            interval = setInterval(() => {
                totalKeysStatsRefresh();
            }, refreshInterval);
            setTotalKeysStatsInterval(interval);
        }
        return () => clearInterval(interval);
    }, [totalKeysStats, refreshInterval]);

    useEffect(() => {
        let interval: any;
        if (connectedClientsStats && refreshInterval > 0) {
            interval = setInterval(() => {
                connectedClientsStatsRefresh();
            }, refreshInterval);
            setConnectedClientsStatsInterval(interval);
        }
        return () => clearInterval(interval);
    }, [connectedClientsStats, refreshInterval]);

    useEffect(() => {
        let interval: any;
        if (blockedClientsStats && refreshInterval > 0) {
            interval = setInterval(() => {
                blockedClientsStatsRefresh();
            }, refreshInterval);
            setBlockedClientsStatsInterval(interval);
        }
        return () => clearInterval(interval);
    }, [blockedClientsStats, refreshInterval]);

    useEffect(() => {
        let interval: any;
        if (memoryUsedStats && refreshInterval > 0) {
            interval = setInterval(() => {
                memoryUsedStatsRefresh();
            }, refreshInterval);
            setMemoryUsedStatsInterval(interval);
        }
        return () => clearInterval(interval);
    }, [memoryUsedStats, refreshInterval]);

    useEffect(() => {
        let interval: any;
        if (slaveLagStats && refreshInterval > 0) {
            interval = setInterval(() => {
                slaveLagStatsRefresh();
            }, refreshInterval);
            setSlaveLagStatsInterval(interval);
        }
        return () => clearInterval(interval);
    }, [slaveLagStats, refreshInterval]);

    useEffect(() => {
        if (interval === 0) {
            // when interval is 0, we must pause the refresh (clear existing intervals)
            clearInterval(commandsPerSecStatsInterval);
            clearInterval(totalKeysStatsInterval);
            clearInterval(connectedClientsStatsInterval);
            clearInterval(blockedClientsStatsInterval);
            clearInterval(memoryUsedStatsInterval);
            clearInterval(slaveLagStatsInterval);
            setCommandsPerSecStatsInterval(0);
            setTotalKeysStatsInterval(0);
            setConnectedClientsStatsInterval(0);
            setBlockedClientsStatsInterval(0);
            setMemoryUsedStatsInterval(0);
            setSlaveLagStatsInterval(0);
        }
        setRefreshInterval(interval);
    }, [interval]);

    useEffect(() => {
        if (hidden) {
            // clear interval to stop updating data if the tab is not active (charts are not visible)
            clearInterval(commandsPerSecStatsInterval);
            clearInterval(totalKeysStatsInterval);
            clearInterval(connectedClientsStatsInterval);
            clearInterval(blockedClientsStatsInterval);
            clearInterval(memoryUsedStatsInterval);
            clearInterval(slaveLagStatsInterval);
            setCommandsPerSecStatsInterval(0);
            setTotalKeysStatsInterval(0);
            setConnectedClientsStatsInterval(0);
            setBlockedClientsStatsInterval(0);
            setMemoryUsedStatsInterval(0);
            setSlaveLagStatsInterval(0);
        } else if (
            commandsPerSecStatsInterval === 0 ||
            totalKeysStatsInterval === 0 ||
            connectedClientsStatsInterval === 0 ||
            blockedClientsStatsInterval === 0 ||
            memoryUsedStatsInterval === 0 ||
            slaveLagStatsInterval === 0
        ) {
            // force refresh if the interval is 0, otherwise, the interval will refresh data soon
            // interval is undefined on the first load
            // interval will be 0 when paused or tab is hidden
            commandsPerSecStatsRefresh();
            totalKeysStatsRefresh();
            connectedClientsStatsRefresh();
            blockedClientsStatsRefresh();
            memoryUsedStatsRefresh();
            slaveLagStatsRefresh();
        }
    }, [hidden]);

    useEffect(() => {
        if (
            commandsPerSecStatsLoading ||
            totalKeysStatsLoading ||
            connectedClientsStatsLoading ||
            blockedClientsStatsLoading ||
            memoryUsedStatsLoading ||
            slaveLagStatsLoading
        ) {
            onLoading && onLoading(true);
        } else {
            onLoading && onLoading(false);
        }
    }, [
        commandsPerSecStatsLoading,
        totalKeysStatsLoading,
        connectedClientsStatsLoading,
        blockedClientsStatsLoading,
        memoryUsedStatsLoading,
        slaveLagStatsLoading,
    ]);

    return (
        <section className={styles.ValkeyDashboard}>
            <Row
                gutter={[
                    32,
                    { xs: 8, sm: 16, md: 24, lg: 32, xl: 40, xxl: 48 },
                ]}
                className={styles.ValkeyDashboardRow}
            >
                <Col
                    className={styles.ValkeyDashboardCol}
                    xs={24}
                    sm={24}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                >
                    <PrometheusValkeyBlockedClientsChart
                        data={blockedClientsStats}
                        loading={blockedClientsStatsLoading}
                        displaySummary={displaySummary}
                    />
                </Col>
                <Col
                    className={styles.ValkeyDashboardCol}
                    xs={24}
                    sm={24}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                >
                    <PrometheusValkeyMemoryUsedChart
                        data={memoryUsedStats}
                        loading={memoryUsedStatsLoading}
                        displaySummary={displaySummary}
                    />
                </Col>
                <Col
                    className={styles.ValkeyDashboardCol}
                    xs={24}
                    sm={24}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                >
                    <PrometheusValkeyConnectedClientsChart
                        data={connectedClientsStats}
                        loading={connectedClientsStatsLoading}
                        displaySummary={displaySummary}
                    />
                </Col>
                <Col
                    className={styles.ValkeyDashboardCol}
                    xs={24}
                    sm={24}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                >
                    <PrometheusValkeyCommandsPerSecChart
                        data={commandsPerSecStats}
                        loading={commandsPerSecStatsLoading}
                        displaySummary={displaySummary}
                    />
                </Col>
                <Col
                    className={styles.ValkeyDashboardCol}
                    xs={24}
                    sm={24}
                    md={8}
                    lg={8}
                    xl={8}
                    xxl={8}
                >
                    <PrometheusValkeyTotalKeysChart
                        data={totalKeysStats}
                        loading={totalKeysStatsLoading}
                        displaySummary={displaySummary}
                    />
                </Col>
                {node?.isMaster() && (
                    <Col
                        className={styles.ValkeyDashboardCol}
                        xs={24}
                        sm={24}
                        md={8}
                        lg={8}
                        xl={8}
                        xxl={8}
                    >
                        <PrometheusValkeySlaveLagChart
                            data={slaveLagStats}
                            loading={slaveLagStatsLoading}
                            displaySummary={displaySummary}
                        />
                    </Col>
                )}
            </Row>
        </section>
    );
}

export default ValkeyDashboard;
